import { useState, useEffect } from "react";
import { pubSub } from "../../ably";
import { CHANNELS } from "../../channel/channels";
import { fetchQuizResult } from "../../api/adminHook";
import { removePollItem, patchPollLocal } from "../../hooks/adminStore.js";
import { updatePresentationState } from "../../hooks/usePresentationSync.js";

// Last non-empty result this admin tab published to quiz-result, keyed by quiz (poll) id.
// Checked before channel history because admin history reads are cached per connection.
const publishedResults = new Map();

const toResultList = (data) =>
  Array.isArray(data) && data.length ? data.map((u) => ({ name: u.name, point: Number(u.point ?? u.points ?? 0), image: u.image ?? u.avatar ?? "" })) : [];

const countQuestions = (quiz) => {
  const keys = quiz?.data && typeof quiz.data === "object" ? Object.keys(quiz.data).filter((k) => /^question\d+$/i.test(k)) : [];
  return keys.length || Number(quiz?.question_count) || 1;
};

// Newest non-empty quiz-result for this exact quiz id, or null. Never matches another quiz.
async function findPublishedResult(pollId) {
  const key = String(pollId);
  if (publishedResults.has(key)) return publishedResults.get(key);
  try {
    const history = await pubSub.getChannelHistory(CHANNELS.QUIZ_RESULT, { limit: 100, maxPages: 1 });
    const hit = (history || []).find((m) => m && m.pollId != null && String(m.pollId) === key && Array.isArray(m.data) && m.data.length > 0);
    return hit ? hit.data : null;
  } catch (e) {
    console.warn("[quiz] reading quiz-result history failed:", e?.message || e);
    return null;
  }
}

export function useInteractiveQuizLogic(ctx) {
  const { interactiveQuizzes, presMode, activeInteractiveQuizzes, turnOffAllQuizzes, closePresentation } = ctx || {};
  const [hasResult, setHasResult] = useState(false);
  const [resultData, setResultData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Whether users have been shown the result (showresult: true + quiz-result published).
  const [resultShown, setResultShown] = useState(false);
  // Quiz id whose card "Show Result" is in progress.
  const [showingResultId, setShowingResultId] = useState(null);

  // The session this page is for. ctx has no top-level sessionId, so read it from the session.
  const currentSessionId = () => ctx?.session?.sessionId ?? ctx?.sessionId ?? window.location.pathname.match(/\/sessiondetails\/([^/]+)/i)?.[1];

  useEffect(() => {
    if (presMode) { setHasResult(false); setResultData([]); setResultShown(false); }
  }, [presMode]);

  const normalize = (res) => {
    let raw = res?.data ?? res;
    if (typeof raw === "string") { try { raw = JSON.parse(raw); } catch { raw = []; } }
    if (raw && typeof raw.data === "string") { try { raw = JSON.parse(raw.data); } catch { /* keep the outer shape */ } }
    if (raw && !Array.isArray(raw) && raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) raw = raw.data;
    else if (!Array.isArray(raw) && Array.isArray(raw?.data)) raw = raw.data;
    let arr = [];
    // cme/poll/result/<pollId> answers { data: [{ user_master_id, name, point }] }.
    if (Array.isArray(raw)) arr = raw.filter((u) => u && typeof u === "object");
    else if (raw && typeof raw === "object") {
      arr = Object.entries(raw).map(([name, points]) => ({ name, point: Number(points) || 0, points: Number(points) || 0 }));
      arr.sort((a, b) => (b.point ?? b.points) - (a.point ?? a.points));
    }
    const mapped = arr.map((u, i) => {
      const name = u.name || `User ${i + 1}`;
      const point = u.point !== undefined ? Number(u.point) || 0 : (u.points !== undefined ? Number(u.points) || 0 : (u.pts ?? 0));
      const image = u.image || u.avatar || "";
      const id = u.id ?? u.user_master_id;
      return {
        ...u,
        id: id != null ? String(id) : String(i + 1),
        name,
        point,
        points: point,
        image,
        avatar: image || (name ? name.charAt(0).toUpperCase() : "U"),
      };
    });
    // Highest score first; equal scores keep the API's order (sort is stable).
    return mapped.sort((a, b) => b.point - a.point);
  };

  // The quiz being presented (picked via a card's Start Presentation) wins; otherwise the
  // previous fallback order.
  const getPresentedPoll = () => {
    const id = ctx?.presTargetId;
    if (id == null) return null;
    return [...(interactiveQuizzes || []), ...(ctx?.session?.polls || [])].find((p) => String(p.id) === String(id)) || null;
  };
  const getActivePoll = () => (getPresentedPoll() || ctx?.session?.polls?.find((p) => p.category === "interactive") || activeInteractiveQuizzes?.[0] || interactiveQuizzes?.[0] || (ctx?.session?.polls || []).find((p) => p.category === "interactive"));

  // Fetch the Poll Result API for the admin's own leaderboard. Does NOT publish:
  // users keep seeing the skeleton until the admin turns Show Result on.
  // The API is keyed by the quiz's own poll id (cme/poll/result/<pollId>), never the session id.
  const getResult = async () => {
    const pollId = getActivePoll()?.id;
    if (pollId == null) {
      setResultData([]);
      setHasResult(true);
      return [];
    }
    try {
      setLoading(true);
      const res = await fetchQuizResult(pollId);
      const data = normalize(res);
      setResultData(data);
      setHasResult(true);
      return data;
    } catch (e) {
      console.error("Failed to fetch quiz result:", e);
      setResultData([]);
      setHasResult(true);
      return [];
    } finally { setLoading(false); }
  };

  // Best-effort publish: pubSub queues while the socket is down and only settles on
  // reconnect, so race it with a timeout - otherwise this await hangs and the Show
  // Result switch stays disabled forever. Returns whether the publish succeeded;
  // only a successful publish may be cached as "delivered".
  const publishResult = async (data, poll = getActivePoll()) => {
    const list = toResultList(data);
    const payload = { data: list, pollId: poll?.id ?? null, isActive: true, timestamp: Math.floor(Date.now() / 1000) };
    let timer = null;
    let published = false;
    try {
      await Promise.race([
        pubSub.publish(CHANNELS.QUIZ_RESULT, payload),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error("publish timed out")), 8000);
        }),
      ]);
      published = true;
    } catch (e) {
      console.error("Failed to publish quiz result:", e?.message || e);
    } finally {
      clearTimeout(timer);
    }
    if (published && poll?.id != null && list.length) publishedResults.set(String(poll.id), list);
    return published;
  };

  // Card "Show Result": put users straight into leaderboard-only mode for this quiz,
  // at any time. Reuses the quiz-result already published for this exact quiz id;
  // calls the Poll Result API only when there is none. The result is (re)published
  // before the state so users have the data when the leaderboard appears.
  const showResultForQuiz = async (quiz) => {
    const sid = currentSessionId();
    if (!quiz || quiz.id == null || !sid || showingResultId) return;
    setShowingResultId(String(quiz.id));
    // Flip the card's switch immediately: the API/publish chain below can take a while
    // (or fail while the socket is down), and the admin must not wait for it to move.
    patchPollLocal(sid, quiz.id, { showresult: true });
    try {
      let list = await findPublishedResult(quiz.id);
      if (!list) {
        console.log(`[quiz] no quiz-result for id=${quiz.id}; calling Poll Result API`);
        list = toResultList(normalize(await fetchQuizResult(quiz.id)));
      } else {
        console.log(`[quiz] reusing quiz-result for id=${quiz.id} (no API call)`);
      }
      const published = await publishResult(list, quiz);
      if (!published) {
        // Viewers never received the result — fall through to the catch below
        // so the optimistic showresult: true is rolled back.
        throw new Error("quiz-result publish failed");
      }
      // Result slide + showresult: true => isApproved: true, showresult: true, intropage: false.
      updatePresentationState(sid, quiz, countQuestions(quiz) + 1, { showresult: true });
    } catch (e) {
      console.error("[quiz] Show Result failed:", e);
      // The optimistic flip must not survive a failure - put the switch back.
      patchPollLocal(sid, quiz.id, { showresult: false });
    } finally {
      setShowingResultId(null);
    }
  };

  // Show Result on: publish the result to quiz-result first, then showresult: true,
  // so users already have the data when their skeleton is replaced.
  // Off: showresult: false, users go back to the skeleton.
  const setShowResult = async (on) => {
    if (on) {
      const data = hasResult ? resultData : await getResult();
      await publishResult(data);
      ctx?.publishResultVisibility?.(true);
      setResultShown(true);
    } else {
      ctx?.publishResultVisibility?.(false);
      setResultShown(false);
    }
  };

  const finish = async () => {
    const sid = ctx?.sessionId || window.location.pathname.match(/\/sessiondetails\/([^/]+)/i)?.[1];
    const poll = getActivePoll();
    const pid = poll?.id;
    const closeForViewers = () => {
      if (pid && sid) {
        try { removePollItem(sid, String(pid)); } catch { /* best-effort local removal */ }
        try {
          return Promise.resolve(
            pubSub.publish(CHANNELS.POLL, { data: [{ isApproved: false, showresult: false, id: poll?.id ?? pid, title: poll?.title ?? "", category: "interactive", points: 0, duration: "10", question_count: poll?.question_count ?? 2, is_shuffle: 0, data: poll?.data || {} }] }),
          );
        } catch {
          return Promise.reject(new Error("close publish failed"));
        }
      }
      return null;
    };
    // Finish closes the quiz for users (isApproved: false). It never publishes the result.
    // Retry the close ONLY when its publish fails — a blind 300ms timer used to
    // re-send every successful publish (duplicate removePollItem + POLL messages).
    const first = closeForViewers();
    if (first) {
      first.catch(() => {
        setTimeout(closeForViewers, 300);
      });
    }
    try { turnOffAllQuizzes(); } catch { /* best-effort cleanup */ }
    try { closePresentation(); } catch { /* best-effort cleanup */ }
    setResultShown(false);
  };

  // Local reset only (e.g. admin went back to a question, which already published showresult: false).
  const resetResultShown = () => setResultShown(false);

  // Card toggle off: leave leaderboard-only mode. isApproved: false closes the quiz
  // for users (showresult: false alone would leave them on the waiting skeleton).
  const hideResultForQuiz = (quiz) => {
    const sid = currentSessionId();
    if (!quiz || quiz.id == null || !sid) return;
    // Same as above: the switch moves now, the publish below catches up.
    patchPollLocal(sid, quiz.id, { showresult: false });
    updatePresentationState(sid, quiz, 999);
  };

  const toggleResultForQuiz = (quiz, on) => (on ? showResultForQuiz(quiz) : hideResultForQuiz(quiz));

  return { hasResult, resultData, loading, resultShown, showingResultId, getResult, setShowResult, showResultForQuiz, toggleResultForQuiz, resetResultShown, finish, normalize };
}
